import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { ArrowUpRight, CircleDot, ExternalLink } from 'lucide-react';
import { db } from '../../firebase/config';
import { cn } from '../../lib/utils';
import SEO from '../SEO';
import { absoluteUrl } from '../../lib/seo';
import { gameLabel, modeLabel } from '../../constants/modes';
import Pagination from '../ui/pagination';
import {
  deriveTournamentStatus,
  getTournamentParticipation,
  normalizeSponsors,
  toDate,
} from './tournament/tournamentHelpers';

const PAGE_SIZE = 6;

const fmt = (d) =>
  d
    ? d
        .toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })
        .toUpperCase()
        .replace(/\./g, '')
    : '—';

const deriveStatus = deriveTournamentStatus;

const statusPill = {
  active: 'border-heat/60 text-heat',
  upcoming: 'border-border text-foreground',
  completed: 'border-border/60 text-muted-foreground',
};

const Tournaments = () => {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const snap = await getDocs(
          query(collection(db, 'tournaments'), orderBy('startDate', 'desc'))
        );
        if (!cancelled) {
          setTournaments(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        }
      } catch {
        // surface gracefully — anonymous reads may be blocked by rules
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const { active, upcoming, finished } = useMemo(() => {
    const a = [],
      u = [],
      f = [];
    tournaments.forEach((t) => {
      const entry = { ...t, status: deriveStatus(t) };
      if (entry.status === 'active') a.push(entry);
      else if (entry.status === 'upcoming') u.push(entry);
      else f.push(entry);
    });
    u.sort((x, y) => (toDate(x.startDate) || 0) - (toDate(y.startDate) || 0));
    a.sort((x, y) => (toDate(x.endDate) || 0) - (toDate(y.endDate) || 0));
    f.sort((x, y) => (toDate(y.endDate) || 0) - (toDate(x.endDate) || 0));
    return { active: a, upcoming: u, finished: f };
  }, [tournaments]);

  const itemList = [...active, ...upcoming, ...finished].slice(0, 50).map((t, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    url: absoluteUrl(`/tournaments/${t.slug || t.id}`),
    name: t.name || 'Tournament',
  }));
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'KZ Tournaments',
    url: absoluteUrl('/tournaments'),
    mainEntity: { '@type': 'ItemList', itemListElement: itemList },
  };

  return (
    <div className="mx-auto w-full max-w-screen-2xl px-4 pb-24 pt-12 sm:px-6">
      <SEO
        title="KZ Tournaments — Active, Upcoming &amp; Past Events"
        description="Browse competitive KZ tournaments across CS2KZ, GOKZ, KZTimer and SimpleKZ. See active brackets, upcoming events and finished standings."
        canonicalPath="/tournaments"
        jsonLd={jsonLd}
      />
      <div className="mb-10 flex flex-col gap-3">
        <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
          <span className="h-px w-6 bg-heat" />
          Operations ledger
        </div>
        <h1 className="font-display text-5xl font-bold tracking-tight sm:text-6xl">Tournaments</h1>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-64 animate-pulse rounded-md border border-border/60 bg-muted/10"
            />
          ))}
        </div>
      ) : tournaments.length === 0 ? (
        <div className="border border-dashed border-border/60 px-6 py-20 text-center">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            Next drop · TBA
          </p>
          <p className="mt-4 text-sm text-muted-foreground">
            No tournaments scheduled yet. Check back soon.
          </p>
        </div>
      ) : (
        <div className="space-y-16">
          <Section title="Live now" items={active} accent />
          <Section title="Upcoming" items={upcoming} />
          <Section title="Completed" items={finished} muted />
        </div>
      )}
    </div>
  );
};

const Section = ({ title, items, accent, muted }) => {
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [items]);

  if (!items || items.length === 0) return null;

  const pageCount = Math.ceil(items.length / PAGE_SIZE);
  const currentPage = Math.min(page, pageCount);
  const visible = items.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <section>
      <div className="mb-6 flex items-center gap-4">
        <span className={cn('h-px w-6', accent ? 'bg-heat' : 'bg-foreground/30')} />
        <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
          {title} <span className="text-foreground/40">· {items.length}</span>
        </h2>
      </div>
      <div className={cn('grid grid-cols-1 gap-6', visible.length > 1 && 'md:grid-cols-2')}>
        {visible.map((t, i) => (
          <TournamentCard
            key={t.id}
            tournament={t}
            muted={muted}
            eager={accent && currentPage === 1 && i === 0}
          />
        ))}
      </div>
      <Pagination page={currentPage} pageCount={pageCount} onPageChange={setPage} />
    </section>
  );
};

const TournamentCard = ({ tournament, muted, eager }) => {
  const start = toDate(tournament.startDate);
  const end = toDate(tournament.endDate);
  const status = tournament.status;
  const participation = getTournamentParticipation(tournament, status);
  return (
    <Link
      to={`/tournaments/${tournament.slug || tournament.id}`}
      className={cn(
        'group relative flex flex-col overflow-hidden rounded-md border border-border/60 bg-background/40 transition-all',
        'hover:border-foreground/40 hover:-translate-y-0.5',
        muted && 'opacity-90 hover:opacity-100'
      )}
    >
      <div className="relative h-48 w-full overflow-hidden border-b border-border/60 sm:h-56">
        {tournament.imageUrl ? (
          <img
            src={tournament.imageUrl}
            alt=""
            loading={eager ? 'eager' : 'lazy'}
            decoding="async"
            fetchpriority={eager ? 'high' : 'low'}
            className="h-full w-full object-cover opacity-75 transition-opacity group-hover:opacity-95"
          />
        ) : (
          <div className="h-full w-full bg-gradient-to-br from-secondary via-background to-background" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent" />

        {tournament.tournamentLogo && (
          <img
            src={tournament.tournamentLogo}
            alt=""
            loading="lazy"
            decoding="async"
            className="absolute left-4 top-4 h-16 w-16 rounded-md border border-border/60 bg-background/70 object-contain p-2 sm:h-20 sm:w-20"
          />
        )}

        <div className="absolute right-4 top-4 flex flex-col items-end gap-2">
          <span
            className={cn(
              'inline-flex items-center gap-1.5 border bg-background/70 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] backdrop-blur',
              statusPill[status] || statusPill.upcoming
            )}
          >
            {status === 'active' && <CircleDot className="h-2.5 w-2.5 animate-pulse-live" />}
            {status}
          </span>
          {normalizeSponsors(tournament.sponsors).some((sponsor) => sponsor.logoUrl) && (
            <div className="hidden gap-1.5 sm:flex">
              {normalizeSponsors(tournament.sponsors)
                .filter((sponsor) => sponsor.logoUrl)
                .slice(0, 3)
                .map((sponsor, i) => (
                  <img
                    key={`${sponsor.logoUrl}-${i}`}
                    src={sponsor.logoUrl}
                    alt={sponsor.name || ''}
                    loading="lazy"
                    decoding="async"
                    className="h-9 max-w-[6rem] rounded-sm bg-background/70 object-contain p-1 backdrop-blur"
                  />
                ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-3 p-5">
        <h3 className="font-display text-xl font-semibold tracking-tight sm:text-2xl">
          {tournament.name}
        </h3>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground tabular-nums">
          {fmt(start)} <span className="mx-2 text-foreground/30">→</span> {fmt(end)}
        </div>
        {(tournament.game || tournament.mode) && (
          <div className="flex flex-wrap gap-2 font-mono text-[10px] uppercase tracking-[0.2em]">
            {tournament.game && (
              <span className="border border-border/60 px-2 py-0.5 text-muted-foreground">
                {gameLabel(tournament.game)}
              </span>
            )}
            {tournament.mode && (
              <span className="border border-border/60 px-2 py-0.5 text-muted-foreground">
                {modeLabel(tournament.mode)}
              </span>
            )}
          </div>
        )}
        {tournament.description && (
          <p className="line-clamp-2 text-sm text-muted-foreground">{tournament.description}</p>
        )}
        <div className="mt-auto flex items-center justify-between pt-2">
          <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground transition-colors group-hover:text-foreground">
            View details
            <ArrowUpRight className="h-3 w-3 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </span>
          {participation && (
            <a
              href={participation.url}
              target="_blank"
              rel="noreferrer noopener"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 rounded-md border border-heat/60 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-heat transition-colors hover:bg-heat hover:text-heat-foreground"
            >
              Participate
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      </div>
    </Link>
  );
};

export default Tournaments;
